/**
 * StructuredRequest API Client
 * HTTP client for interacting with the StructuredRequest API
 */

import axios, { AxiosInstance } from 'axios';
import type {
  RequestTemplate,
  FormResponse,
  ResponseRevision,
  CreateRequestDto,
  UpdateRequestDto,
  SubmitResponseDto,
  PrefillResponseDto,
  UpdateResponseDataDto,
  ApiResponse,
  PaginatedResponse,
  PublicRequestTemplate,
} from '@structured-request/core';

export interface ApiClientConfig {
  /** Base URL for API calls (default: /api) */
  baseUrl?: string;
  /** Access token for authentication */
  accessToken?: string;
  /** Custom axios instance */
  axiosInstance?: AxiosInstance;
  /** Timeout in milliseconds */
  timeout?: number;
}

export class StructuredRequestClient {
  private client: AxiosInstance;

  constructor(config: ApiClientConfig = {}) {
    const baseURL = config.baseUrl ?? '/api';

    this.client =
      config.axiosInstance ??
      axios.create({
        baseURL,
        timeout: config.timeout ?? 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

    if (config.accessToken) {
      this.setAccessToken(config.accessToken);
    }
  }

  /**
   * Set or update the access token for authenticated requests
   */
  setAccessToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Clear the access token
   */
  clearAccessToken(): void {
    delete this.client.defaults.headers.common['Authorization'];
  }

  // =========================================================================
  // Request Templates (Admin)
  // =========================================================================

  /**
   * Get all request templates
   */
  async getRequests(params?: {
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<RequestTemplate>> {
    const response = await this.client.get<PaginatedResponse<RequestTemplate>>(
      '/requests',
      { params }
    );
    return response.data;
  }

  /**
   * Get a single request template by ID
   */
  async getRequest(id: string): Promise<RequestTemplate> {
    const response = await this.client.get<RequestTemplate>(`/requests/${id}`);
    return response.data;
  }

  /**
   * Create a new request template
   */
  async createRequest(data: CreateRequestDto): Promise<RequestTemplate> {
    const response = await this.client.post<RequestTemplate>('/requests', data);
    return response.data;
  }

  /**
   * Update an existing request template
   */
  async updateRequest(id: string, data: UpdateRequestDto): Promise<RequestTemplate> {
    const response = await this.client.put<RequestTemplate>(`/requests/${id}`, data);
    return response.data;
  }

  /**
   * Delete a request template
   */
  async deleteRequest(id: string): Promise<void> {
    await this.client.delete(`/requests/${id}`);
  }

  /**
   * Activate a request template
   */
  async activateRequest(id: string): Promise<RequestTemplate> {
    return this.updateRequest(id, { status: 'active' });
  }

  /**
   * Pause a request template
   */
  async pauseRequest(id: string): Promise<RequestTemplate> {
    return this.updateRequest(id, { status: 'paused' });
  }

  // =========================================================================
  // Responses (Admin)
  // =========================================================================

  /**
   * Get all responses for a request template
   */
  async getResponses(
    requestId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<FormResponse>> {
    const response = await this.client.get<PaginatedResponse<FormResponse>>(
      `/requests/${requestId}/responses`,
      { params }
    );
    return response.data;
  }

  /**
   * Get a single response by ID
   */
  async getResponse(requestId: string, responseId: string): Promise<FormResponse> {
    const response = await this.client.get<FormResponse>(
      `/requests/${requestId}/responses/${responseId}`
    );
    return response.data;
  }

  /**
   * Delete a response
   */
  async deleteResponse(requestId: string, responseId: string): Promise<void> {
    await this.client.delete(`/requests/${requestId}/responses/${responseId}`);
  }

  // =========================================================================
  // Public (Respondent)
  // =========================================================================

  /**
   * Get public request template for responding
   */
  async getPublicRequest(id: string): Promise<PublicRequestTemplate> {
    const response = await this.client.get<PublicRequestTemplate>(`/public/requests/${id}`);
    return response.data;
  }

  /**
   * Submit a response to a request
   */
  async submitResponse(
    requestId: string,
    data: SubmitResponseDto
  ): Promise<ApiResponse<FormResponse>> {
    const response = await this.client.post<ApiResponse<FormResponse>>(
      `/public/requests/${requestId}/responses`,
      data
    );
    return response.data;
  }

  /**
   * Get a single submitted response (for direct response URL / edit mode)
   */
  async getPublicResponse(requestId: string, responseId: string): Promise<FormResponse> {
    const response = await this.client.get<FormResponse>(
      `/public/requests/${requestId}/responses/${responseId}`
    );
    return response.data;
  }

  /**
   * Edit an already-submitted response or finalise a draft.
   * The server enforces responseEditMode on the parent request template.
   */
  async updatePublicResponse(
    requestId: string,
    responseId: string,
    data: UpdateResponseDataDto | SubmitResponseDto
  ): Promise<ApiResponse<FormResponse>> {
    const response = await this.client.put<ApiResponse<FormResponse>>(
      `/public/requests/${requestId}/responses/${responseId}`,
      data
    );
    return response.data;
  }

  // =========================================================================
  // Prefill (Admin / Integration)
  // =========================================================================

  /**
   * Create a server-side draft pre-populated with known field values.
   * Returns a draft response whose ID should be embedded in the form URL as
   * the responseId path segment:
   *   /respond/{requestId}/r/{draftId}
   * Requires x-api-key header.
   */
  async createPrefill(
    requestId: string,
    data: PrefillResponseDto,
    apiKey: string
  ): Promise<ApiResponse<FormResponse>> {
    const response = await this.client.post<ApiResponse<FormResponse>>(
      `/requests/${requestId}/prefill`,
      data,
      { headers: { 'x-api-key': apiKey } }
    );
    return response.data;
  }

  // =========================================================================
  // Admin — lock / history
  // =========================================================================

  /**
   * Lock or unlock a response against editing.
   * Requires x-api-key header.
   */
  async setResponseLock(
    requestId: string,
    responseId: string,
    locked: boolean,
    changedBy?: string,
    apiKey?: string
  ): Promise<ApiResponse<FormResponse>> {
    const headers = apiKey ? { 'x-api-key': apiKey } : {};
    const response = await this.client.patch<ApiResponse<FormResponse>>(
      `/requests/${requestId}/responses/${responseId}/lock`,
      { locked, changedBy },
      { headers }
    );
    return response.data;
  }

  /**
   * Get the full revision history of a response.
   */
  async getResponseHistory(
    requestId: string,
    responseId: string
  ): Promise<ApiResponse<ResponseRevision[]>> {
    const response = await this.client.get<ApiResponse<ResponseRevision[]>>(
      `/requests/${requestId}/responses/${responseId}/history`
    );
    return response.data;
  }

  // =========================================================================
  // Utilities
  // =========================================================================

  /**
   * Generate a shareable URL to the public form (new submission).
   * When requiresAuth is true the /secure/ prefix is added so the public SWA
   * enforces Entra ID authentication before allowing access.
   */
  generateFormUrl(requestId: string, baseUrl?: string, requiresAuth?: boolean): string {
    const base = baseUrl ?? window.location.origin;
    return requiresAuth
      ? `${base}/secure/respond/${requestId}`
      : `${base}/respond/${requestId}`;
  }

  /**
   * Generate a direct URL to a specific response (view / edit).
   */
  generateResponseUrl(requestId: string, responseId: string, baseUrl?: string, requiresAuth?: boolean): string {
    const base = baseUrl ?? window.location.origin;
    return requiresAuth
      ? `${base}/secure/respond/${requestId}/r/${responseId}`
      : `${base}/respond/${requestId}/r/${responseId}`;
  }

  /**
   * Check API health
   */
  async healthCheck(): Promise<boolean> {
    try {
      await this.client.get('/health');
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Create a pre-configured API client instance
 */
export function createApiClient(config?: ApiClientConfig): StructuredRequestClient {
  return new StructuredRequestClient(config);
}

// Default export for convenience
export default StructuredRequestClient;
